import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../core/services/settings.service';
import { AppUser } from '../../core/models/settings.models';
import { confirmAction } from '../../core/utils/confirm.util';
import {
  emailValidationMessage,
  passwordValidationMessage,
} from '../../core/utils/input-validation.util';
// Define Permission type here since it's not exported from models
export type Permission =
  | 'all'
  | 'view_inventory'
  | 'create_inventory'
  | 'edit_inventory'
  | 'delete_inventory'
  | 'view_users'
  | 'create_users'
  | 'edit_users'
  | 'delete_users'
  | 'view_reports'
  | 'create_reports'
  | 'create_orders'
  | 'edit_orders'
  | 'delete_orders'
  | 'view_settings'
  | 'edit_settings';

export interface UserRole {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
}

export const ALL_UI_PERMISSIONS: Permission[] = [
  'view_inventory',
  'create_inventory',
  'edit_inventory',
  'delete_inventory',
  'view_users',
  'create_users',
  'edit_users',
  'delete_users',
  'view_reports',
  'create_reports',
  'create_orders',
  'edit_orders',
  'delete_orders',
  'view_settings',
  'edit_settings',
];

export const AVAILABLE_ROLES: UserRole[] = [
  {
    id: 'admin',
    name: 'Administrator',
    description: 'Full system access',
    permissions: ['all'],
  },
  {
    id: 'manager',
    name: 'Manager',
    description: 'Manage inventory and users',
    permissions: [
      'view_inventory',
      'create_inventory',
      'edit_inventory',
      'delete_inventory',
      'view_users',
      'create_users',
      'edit_users',
      'delete_users',
      'view_reports',
      'create_reports',
    ],
  },
  {
    id: 'warehouse',
    name: 'Warehouse Staff',
    description: 'Manage warehouse operations',
    permissions: [
      'view_inventory',
      'create_inventory',
      'edit_inventory',
      'delete_inventory',
      'view_reports',
    ],
  },
  {
    id: 'sales',
    name: 'Sales Staff',
    description: 'View inventory and create orders',
    permissions: ['view_inventory', 'view_reports', 'create_orders'],
  },
  {
    id: 'viewer',
    name: 'Viewer',
    description: 'Read-only access',
    permissions: ['view_inventory', 'view_reports'],
  },
];

/** When API role has no RolePermissions, infer defaults from the role name. */
export function defaultPermissionsForRoleName(roleName: string): Permission[] {
  const key = roleName.trim().toLowerCase().replace(/[\s_-]+/g, '');

  if (
    key.includes('admin') ||
    key === 'administrator' ||
    key.includes('companyadministrator')
  ) {
    return [...ALL_UI_PERMISSIONS];
  }

  if (key.includes('sales')) {
    return [...AVAILABLE_ROLES.find((r) => r.id === 'sales')!.permissions];
  }

  if (
    key.includes('clerk') ||
    key.includes('warehouse') ||
    key.includes('storekeep') ||
    key === 'inventoryclerk'
  ) {
    return [...AVAILABLE_ROLES.find((r) => r.id === 'warehouse')!.permissions];
  }

  if (key.includes('equipment')) {
    return [
      'view_inventory',
      'create_inventory',
      'edit_inventory',
      'view_reports',
    ];
  }

  if (
    key.includes('auditor') ||
    key.includes('finance') ||
    key.includes('viewer')
  ) {
    return [...AVAILABLE_ROLES.find((r) => r.id === 'viewer')!.permissions];
  }

  if (key.includes('manager')) {
    return [...AVAILABLE_ROLES.find((r) => r.id === 'manager')!.permissions];
  }

  return [...AVAILABLE_ROLES.find((r) => r.id === 'viewer')!.permissions];
}

function expandRolePermissions(permissions: Permission[]): Permission[] {
  if (permissions.includes('all')) {
    return [...ALL_UI_PERMISSIONS];
  }
  return [...permissions];
}

@Component({
  selector: 'app-user-permissions',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './user-permissions.component.html',
  styleUrls: ['../../settings-shared.css', './user-permissions.component.css'],
})
export class UserPermissionsComponent implements OnInit {
  private readonly settings = inject(SettingsService);

  protected readonly users = this.settings.allUsers;
  protected readonly branches = this.settings.companyBranches;
  protected readonly availableRoles = computed(() => {
    const fromApi = this.settings.allRoles();
    if (fromApi.length) {
      return fromApi.map((role) => {
        const fromApiPerms = (role.permissions ?? []) as Permission[];
        const permissions = fromApiPerms.length
          ? expandRolePermissions(fromApiPerms)
          : defaultPermissionsForRoleName(role.name);
        return {
          id: role.id,
          name: role.name,
          description: role.description,
          permissions,
        };
      });
    }
    return AVAILABLE_ROLES.map((role) => ({
      ...role,
      permissions: expandRolePermissions(role.permissions),
    }));
  });

  protected readonly permissionCatalog = computed(() => {
    const known = new Map(
      this.availablePermissions.map((permission) => [permission.id, permission]),
    );
    const codes = new Set<string>();
    for (const role of this.availableRoles()) {
      for (const code of role.permissions ?? []) {
        if (code && code !== 'all') {
          codes.add(code);
        }
      }
    }
    for (const user of this.users()) {
      for (const code of user.permissions ?? []) {
        if (code && code !== 'all') {
          codes.add(code);
        }
      }
    }
    for (const code of codes) {
      if (!known.has(code as Permission)) {
        known.set(code as Permission, {
          id: code as Permission,
          label: code.replace(/[_.]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          icon: 'bi-key',
          description: code,
        });
      }
    }
    return [...known.values()];
  });

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');
  protected readonly toastIsError = signal(false);
  protected readonly showPermissionsModal = signal(false);

  protected selectedUserPermissions: Permission[] = [];
  protected selectedUserRoleId: string = '';

  protected userForm: {
    name: string;
    email: string;
    password: string;
    role: string;
    branch: string;
    status: 'active' | 'inactive';
    permissions: Permission[];
  } = {
    name: '',
    email: '',
    password: '',
    role: '',
    branch: '',
    status: 'active',
    permissions: [],
  };

  protected availablePermissions = [
    {
      id: 'view_inventory' as Permission,
      label: 'View Inventory',
      icon: 'bi-box',
      description: 'View inventory items',
    },
    {
      id: 'create_inventory' as Permission,
      label: 'Create Inventory',
      icon: 'bi-plus-circle',
      description: 'Add new inventory items',
    },
    {
      id: 'edit_inventory' as Permission,
      label: 'Edit Inventory',
      icon: 'bi-pencil',
      description: 'Modify inventory items',
    },
    {
      id: 'delete_inventory' as Permission,
      label: 'Delete Inventory',
      icon: 'bi-trash',
      description: 'Remove inventory items',
    },
    {
      id: 'view_users' as Permission,
      label: 'View Users',
      icon: 'bi-people',
      description: 'View user accounts',
    },
    {
      id: 'create_users' as Permission,
      label: 'Create Users',
      icon: 'bi-person-plus',
      description: 'Add new users',
    },
    {
      id: 'edit_users' as Permission,
      label: 'Edit Users',
      icon: 'bi-person-gear',
      description: 'Modify user accounts',
    },
    {
      id: 'delete_users' as Permission,
      label: 'Delete Users',
      icon: 'bi-person-x',
      description: 'Remove user accounts',
    },
    {
      id: 'view_reports' as Permission,
      label: 'View Reports',
      icon: 'bi-graph-up',
      description: 'Access reports',
    },
    {
      id: 'create_reports' as Permission,
      label: 'Create Reports',
      icon: 'bi-file-earmark-bar-graph',
      description: 'Generate reports',
    },
    {
      id: 'create_orders' as Permission,
      label: 'Create Orders',
      icon: 'bi-cart-plus',
      description: 'Place orders',
    },
    {
      id: 'edit_orders' as Permission,
      label: 'Edit Orders',
      icon: 'bi-cart',
      description: 'Modify orders',
    },
    {
      id: 'delete_orders' as Permission,
      label: 'Delete Orders',
      icon: 'bi-cart-x',
      description: 'Remove orders',
    },
    {
      id: 'view_settings' as Permission,
      label: 'View Settings',
      icon: 'bi-gear',
      description: 'Access settings',
    },
    {
      id: 'edit_settings' as Permission,
      label: 'Edit Settings',
      icon: 'bi-gear-wide-connected',
      description: 'Modify settings',
    },
  ];

  protected readonly branchNameById = computed(() => {
    const map = new Map<string, string>();
    for (const branch of this.branches()) {
      map.set(branch.id, branch.name);
    }
    return map;
  });

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  getBranchName(branchId: string): string {
    return this.branchNameById().get(branchId) ?? '—';
  }

  // Helper method to get user permissions safely
  getUserPermissions(user: any): Permission[] {
    return user.permissions || [];
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: any): void {
    this.editingId.set(row.id);
    this.userForm = {
      name: row.name || '',
      email: row.email || '',
      password: '',
      role: row.role || '',
      branch: row.branch || '',
      status: row.status || 'active',
      permissions: row.permissions || [],
    };
    this.selectedUserRoleId = row.role || '';
    this.selectedUserPermissions = row.permissions || [];
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
    this.showPermissionsModal.set(false);
  }

  openPermissionsModal(): void {
    this.selectedUserPermissions = [...this.userForm.permissions];
    this.showPermissionsModal.set(true);
  }

  togglePermission(permission: Permission): void {
    const index = this.selectedUserPermissions.indexOf(permission);
    if (index > -1) {
      this.selectedUserPermissions.splice(index, 1);
    } else {
      this.selectedUserPermissions.push(permission);
    }
  }

  hasPermission(permission: Permission): boolean {
    return this.selectedUserPermissions.includes(permission);
  }

  savePermissions(): void {
    this.userForm.permissions = [...this.selectedUserPermissions];
    this.showPermissionsModal.set(false);
  }

  onRoleChange(roleId: string): void {
    this.userForm.role = roleId;
    if (!roleId) {
      this.selectedUserPermissions = [];
      this.userForm.permissions = [];
      return;
    }

    const selectedRole = this.availableRoles().find(
      (r) => r.id === roleId || r.name === roleId,
    );
    const permissions = selectedRole?.permissions?.length
      ? expandRolePermissions(selectedRole.permissions)
      : defaultPermissionsForRoleName(selectedRole?.name || roleId);

    this.selectedUserPermissions = [...permissions];
    this.userForm.permissions = [...permissions];
  }

  getRoleName(roleId: string): string {
    const roles = this.availableRoles();
    const role = roles.find((r) => r.id === roleId || r.name === roleId);
    return role ? role.name : roleId || '—';
  }

  getPermissionCount(permissions: Permission[]): number {
    return permissions?.length || 0;
  }

  getPermissionLabel(permission: Permission): string {
    const found = this.permissionCatalog().find((p) => p.id === permission);
    return found ? found.label : permission;
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.userForm.name || !this.userForm.email) {
      this.showNotice('Name and Email are required', true);
      return;
    }

    const emailError = emailValidationMessage(this.userForm.email, true);
    if (emailError) {
      this.showNotice(emailError, true);
      return;
    }

    if (!this.userForm.branch) {
      this.showNotice('A user must be assigned to a branch.', true);
      return;
    }

    const passwordError = passwordValidationMessage(
      this.userForm.password,
      !editingId,
    );
    if (passwordError) {
      this.showNotice(passwordError, true);
      return;
    }

    if (!this.userForm.role) {
      this.showNotice('Role is required.', true);
      return;
    }

    const payload = {
      name: this.userForm.name,
      email: this.userForm.email,
      password: this.userForm.password,
      role: this.userForm.role,
      branch: this.userForm.branch,
      status: this.userForm.status,
      permissions: this.userForm.permissions || [],
    };

    const onDone = (result: { success: boolean; message: string }) => {
      this.showNotice(result.message, !result.success);
      if (result.success) {
        this.closeModal();
      }
    };

    if (editingId) {
      this.settings.updateUser(editingId, payload).subscribe((result) => {
        if (!result.success) {
          onDone(result);
          return;
        }

        // Permissions are stored on the role (RolePermissions), not per user.
        this.settings
          .updateRolePermissions(payload.role, payload.permissions)
          .subscribe((permResult) => {
            onDone({
              success: permResult.success,
              message: permResult.success
                ? 'User and role permissions updated successfully.'
                : permResult.message || result.message,
            });
          });
      });
      return;
    }

    this.settings.addUser(payload).subscribe(onDone);
  }

  deleteRow(row: any): void {
    if (!confirmAction(`Delete "${row.name}"? This cannot be undone.`)) {
      return;
    }

    this.settings.deleteUser(row.id).subscribe((result) => {
      this.showNotice(result.message, !result.success);
    });
  }

  private resetForm(): void {
    this.userForm = {
      name: '',
      email: '',
      password: '',
      role: '',
      branch: '',
      status: 'active',
      permissions: [],
    };
    this.selectedUserPermissions = [];
    this.selectedUserRoleId = '';
  }

  private showNotice(message: string, isError = false): void {
    this.toastIsError.set(isError);
    this.toastMessage.set(message);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }
}
